/**
 * Button
 * ──────
 * The one button. Five variants, three sizes, a loading state that keeps
 * the width stable, and an optional icon on either side. Renders a
 * react-router <Link> when given `to`, an <a> when given `href`, otherwise
 * a <button type="button">.
 *
 *   <Button onClick={save} loading={saving}>Save</Button>
 *   <Button variant="secondary" icon={<LuFilter />}>Filters</Button>
 *   <Button variant="danger" size="sm">Delete</Button>
 *   <Button to="/tickets/create" icon={<LuPlus />}>New ticket</Button>
 *
 * Variants: primary (the accent blue, one per view), secondary (white with
 * a line, the default for everything else), ghost (no chrome, toolbars and
 * icon buttons), danger (destructive), link (inline text action).
 */
import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { LuLoaderCircle } from 'react-icons/lu';

const BASE =
  'inline-flex items-center justify-center gap-2 font-manrope font-semibold whitespace-nowrap select-none ' +
  'rounded-lg border transition-colors duration-150 cursor-pointer ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

const VARIANTS = {
  primary: 'bg-accent border-accent text-white hover:bg-accent-hover hover:border-accent-hover',
  secondary: 'bg-white border-line-2 text-ink hover:bg-canvas hover:border-line-2',
  ghost: 'bg-transparent border-transparent text-text-2 hover:bg-canvas-3 hover:text-ink',
  danger: 'bg-danger border-danger text-white hover:bg-danger-strong hover:border-danger-strong',
  link: 'bg-transparent border-transparent text-accent hover:underline px-0 h-auto',
};

const SIZES = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-5 text-[15px]',
};

/* Square icon-only buttons. */
const ICON_SIZES = {
  sm: 'h-8 w-8 p-0',
  md: 'h-9 w-9 p-0',
  lg: 'h-11 w-11 p-0',
};

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    iconRight,
    iconOnly = false,
    loading = false,
    disabled = false,
    block = false,
    className = '',
    children,
    to,
    href,
    type = 'button',
    ...rest
  },
  ref
) {
  const sizeClass = variant === 'link' ? '' : iconOnly ? ICON_SIZES[size] : SIZES[size];
  const cls = `${BASE} ${VARIANTS[variant] || VARIANTS.primary} ${sizeClass} ${block ? 'w-full' : ''} ${className}`.trim();
  const isDisabled = disabled || loading;

  const content = (
    <>
      {loading ? (
        <LuLoaderCircle className="animate-spin shrink-0" aria-hidden="true" />
      ) : (
        icon && <span className="shrink-0 inline-flex" aria-hidden="true">{icon}</span>
      )}
      {children && (iconOnly ? <span className="sr-only">{children}</span> : children)}
      {iconRight && !loading && <span className="shrink-0 inline-flex" aria-hidden="true">{iconRight}</span>}
    </>
  );

  if (to && !isDisabled) {
    return (
      <Link ref={ref} to={to} className={cls} {...rest}>
        {content}
      </Link>
    );
  }
  if (href && !isDisabled) {
    return (
      <a ref={ref} href={href} className={cls} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      className={cls}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
